import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Button, Flex, Text, Box, Spinner } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { PDFDocument, PDFName, PDFString, PDFHexString } from 'pdf-lib';

import { getVcMetadata, getVcQrcode } from '../../Utils/frontStageApi';
import Toast from './Toast';
import uploadOkIcon from '../../Resources/Images/Icons/upload_ok.svg';
import visibleIcon from '../../Resources/Images/Icons/visible_icon.svg';
import invisibleIcon from '../../Resources/Images/Icons/invisible_icon.svg';
import verifySuccessIcon from '../../Resources/Images/Icons/veri_success.svg';

interface UploadSectionProps {
  status: number;
  setStatus: React.Dispatch<React.SetStateAction<number>>;
  showMetadata: boolean;
  setShowMetadata: React.Dispatch<React.SetStateAction<boolean>>;
}

type ToastType = 'overSize' | 'networkError' | 'forgotCode' | 'invalidFormat' | 'dataError' | 'multipleFiles' | null;
type FailReason = 'network' | 'server' | null;

const MAX_MB = 2;

const REQUIRED_METADATA_KEYS = [
  'IssueCode',
  'CategoryCode',
  'VerifyCode',
  'VersionCode',
  'CertID',
  'Other',
  'encrypted_metadata',
] as const;

const METADATA_LABEL: Record<string, string> = {
  chinese_name: '姓名',
  e_chinese_name: '姓名(英)',
  birth_date: '生日',
  student_id: '學號',
  graduate_univ: '畢業學校',
  e_graduate_univ: '畢業學校(英)',
  graduate_colg: '畢業學院',
  e_graduate_colg: '畢業學院(英)',
  graduate_dept: '畢業系所_學位學程_組別',
  e_graduate_dept: '畢業系所_學位學程_組別(英)',
  degree: '授予學位名稱',
  e_degree: '授予學位名稱(英)',
  cert_num: '證書字號',
  e_cert_num: '證書字號(英)',
  graduate_year_month: '畢業年月',
  minor_d_major: '輔系雙主修',
  e_minor_d_major: '輔系雙主修(英)',
  reissue_date: '補發日期',
  reissue_cert_num: '補證字號',
  nationality: '國籍',
  e_graduate_acad: '學制(英)',
  original_class: '入學班別',
  identify_id: '身分證統一編號',
};

/* PDF 工具函式 */

/** PDF Info dict 的值統一轉為字串 */
const normalizePdfValue = (value: unknown): string => {
  if (!value) return '';
  if (value instanceof PDFString || value instanceof PDFHexString) {
    try {
      return value.decodeText?.() ?? value.asString?.() ?? String(value);
    } catch {
      return String(value);
    }
  }
  if (typeof (value as any).asString === 'function') {
    try {
      return (value as any).asString();
    } catch {
      return String(value);
    }
  }
  return String(value);
};

/**
 * 讀取 PDF Info dict 並驗證必要欄位是否存在
 * @returns true = 合法憑證 PDF；false = 格式不符
 */
const validateCertPdf = async (file: File): Promise<boolean> => {
  if (!file.name.toLowerCase().endsWith('.pdf')) return false;
  try {
    const pdfDoc = await PDFDocument.load(await file.arrayBuffer(), { updateMetadata: false });
    const infoRef = (pdfDoc.context as any)?.trailerInfo?.Info;
    if (!infoRef) return false;
    const infoDict = pdfDoc.context.lookup(infoRef);
    if (!infoDict || typeof infoDict.entries !== 'function') return false;

    const infoMap: Record<string, string> = {};
    for (const [key, val] of infoDict.entries()) {
      const k = key instanceof PDFName ? key.decodeText() : String(key);
      infoMap[k] = normalizePdfValue(val).trim();
    }
    return REQUIRED_METADATA_KEYS.every((k) => !!infoMap[k]);
  } catch {
    return false;
  }
};

/* Button 樣式 helper */
const btnBase = {
  px: '12px',
  py: '8px',
  h: '37px',
  fontFamily: "'Open Sans'",
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '150%',
  letterSpacing: '0.01em',
  borderRadius: '4px',
};

const btnOutlineBlue = {
  ...btnBase,
  bg: 'transparent',
  border: '1px solid #00AAFF',
  color: '#00AAFF',
  _hover: { bg: '#E6F7FF' },
};
const btnOutlineRed = {
  ...btnBase,
  bg: 'transparent',
  border: '1px solid #D21C1B',
  color: '#D21C1B',
  _hover: { bg: '#FFF0F0' },
};
const btnSolidBlue = { ...btnBase, bg: '#00AAFF', border: 'none', color: '#FFFFFF', _hover: { bg: '#0099EE' } };

/* 共用 Panel 外框 */
const MainPanel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Flex
    w="100%"
    maxW="1040px"
    minH={{ base: '300px', md: '540px' }}
    bg="#FFF9D0"
    borderRadius="10px"
    direction="column"
    align="center"
    justify="center"
    gap="30px"
    px="20px"
  >
    {children}
  </Flex>
);

/* 上傳 PDF 面板 */
type UploadPdfPanelProps = {
  onFile: (file: File) => void | Promise<void>;
  onBrowse: () => void;
  onMultipleFiles: () => void;
};

const UploadPdfPanel: React.FC<UploadPdfPanelProps> = ({ onFile, onBrowse, onMultipleFiles }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void onFile(file);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onBrowse();

    /* 只接受單一檔案，多個直接拒絕 */
    if (e.dataTransfer.files.length > 1) {
      onMultipleFiles();
      return;
    }

    const file = e.dataTransfer.files?.[0];
    if (file) void onFile(file);
  };

  return (
    <Flex
      w="100%"
      maxW="1040px"
      minH={{ base: '300px', md: '540px' }}
      bg="#FFF9D0"
      borderRadius="10px"
      direction="column"
      align="center"
      justify="center"
      gap="0"
      px="20px"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* 主文字*/}
      <Text
        fontFamily="'Open Sans'"
        fontWeight={600}
        fontSize="20px"
        lineHeight="120%"
        letterSpacing="0.01em"
        color="#000000"
        style={{ color: '#000000' }}
        textAlign="center"
      >
        {t('application.drag.0')}
      </Text>
      <Text
        mt="15px"
        fontFamily="'Open Sans'"
        fontWeight={600}
        fontSize="20px"
        lineHeight="120%"
        letterSpacing="0.01em"
        color="#000000"
        style={{ color: '#000000' }}
        textAlign="center"
      >
        {t('application.drag.1')}
      </Text>

      {/* 按鈕列 */}
      <Flex mt="30px" direction="row" align="center" justify="center" gap="12px">
        <Text
          fontFamily="'Open Sans'"
          fontWeight={400}
          fontSize="16px"
          lineHeight="150%"
          letterSpacing="0.01em"
          color="#000000"
          style={{ color: '#000000' }}
        >
          {t('application.drag.2')}
        </Text>
        <Button
          {...btnSolidBlue}
          w="81px"
          onClick={() => {
            onBrowse();
            inputRef.current?.click();
          }}
        >
          {t('application.browse')}
        </Button>
        <input ref={inputRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={handleChange} />
      </Flex>
    </Flex>
  );
};

/* 身分驗證面板 */
interface IdentityVerificationPanelProps {
  onBack: () => void;
  onConfirm: (code: string) => void;
  codeErrorMsg: string;
  onForgotCode: () => void;
}

const IdentityVerificationPanel: React.FC<IdentityVerificationPanelProps> = ({
  onBack,
  onConfirm,
  codeErrorMsg,
  onForgotCode,
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [showCode, setShowCode] = useState(false);
  const canSubmit = code.trim().length > 0;

  return (
    <MainPanel>
      {/* 上傳成功圖示 */}
      <Flex direction="column" align="center" gap="10px">
        <Box w="200px" h="200px">
          <img src={uploadOkIcon} alt="upload ok" width="200" height="200" />
        </Box>
        <Text
          fontFamily="'Open Sans'"
          fontWeight={600}
          fontSize="20px"
          lineHeight="120%"
          letterSpacing="0.01em"
          color="#28A138"
          style={{ color: '#28A138' }}
        >
          {t('application.uploadSuccess')}
        </Text>
      </Flex>

      {/* 驗證碼輸入區 */}
      <Flex direction="column" align="center" gap="12px" w="300px">
        {/* Input field */}
        <Flex direction="column" gap="4px" w="300px">
          <Flex
            boxSizing="border-box"
            direction="row"
            align="center"
            px="12px"
            gap="8px"
            w="300px"
            h="44px"
            bg="#FFFFFF"
            border={`1px solid ${codeErrorMsg ? '#D21C1B' : '#A8B8C2'}`}
            borderRadius="4px"
          >
            <Box
              as="input"
              className="dc-code-input"
              flex="1"
              minW="0"
              h="100%"
              type={showCode ? 'text' : 'password'}
              value={code}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
              placeholder={t('application.codeInputLabel')}
              autoComplete="off"
              fontFamily="'Open Sans'"
              fontSize="14px"
              color="#0A0A25"
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                padding: '0 8px 0 0',
                boxShadow: 'none',
                width: '100%',
                height: '100%',
                color: '#0A0A25',
              }}
            />
            {/* 顯示/隱藏切換 */}
            <Box
              as="button"
              type="button"
              onClick={() => setShowCode((v) => !v)}
              cursor="pointer"
              w="24px"
              h="24px"
              flexShrink={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
              }}
            >
              <img src={showCode ? invisibleIcon : visibleIcon} alt="toggle" width="24" height="24" />
            </Box>
          </Flex>

          {/* 錯誤訊息 */}
          {codeErrorMsg && (
            <Text
              fontFamily="'Open Sans'"
              fontWeight={400}
              fontSize="14px"
              lineHeight="150%"
              letterSpacing="0.01em"
              color="#D21C1B"
              style={{ color: '#D21C1B' }}
            >
              {codeErrorMsg}
            </Text>
          )}
        </Flex>

        {/* 操作按鈕列 */}
        <Flex direction="row" align="center" gap="20px">
          <Button {...btnOutlineRed} onClick={onForgotCode}>
            {t('application.forgotCode')}
          </Button>
          <Button {...btnOutlineBlue} onClick={onBack}>
            {t('application.back')}
          </Button>
          <Button
            {...btnSolidBlue}
            bg={canSubmit ? '#00AAFF' : '#E6F7FF'}
            cursor={canSubmit ? 'pointer' : 'not-allowed'}
            _hover={canSubmit ? { bg: '#0099EE' } : {}}
            isDisabled={!canSubmit}
            onClick={() => onConfirm(code)}
          >
            {t('application.confirm')}
          </Button>
        </Flex>
      </Flex>
    </MainPanel>
  );
};

/* Metadata 確認面板 */
interface MetadataConfirmPanelProps {
  metadata: Record<string, string>;
  onConfirm: () => void;
  onBack: () => void;
  onDataError: () => void;
}

const MetadataConfirmPanel: React.FC<MetadataConfirmPanelProps> = ({ metadata, onConfirm, onBack, onDataError }) => {
  const { t } = useTranslation();
  const entries = Object.entries(metadata);

  const renderCell = (entry: [string, string] | undefined, isKey: boolean) => {
    const w = isKey ? '170px' : '240px';
    return (
      <Flex
        direction="column"
        justify="center"
        align="flex-start"
        w={w}
        pt="1px"
        borderBottom="1px solid #00AAFF"
      >
        <Flex align="center" px="12px" py="10px" gap="10px" w={w} minH="58px">
          {entry ? (
            <Text
              fontFamily="'Open Sans'"
              fontWeight={isKey ? 600 : 400}
              fontSize="16px"
              lineHeight={isKey ? '120%' : '150%'}
              letterSpacing="0.01em"
              color={isKey ? '#006699' : '#00292B'}
              style={{ color: isKey ? '#006699' : '#00292B' }}
              wordBreak="break-word"
            >
              {isKey ? (METADATA_LABEL[entry[0]] ?? entry[0]) : entry[1]}
            </Text>
          ) : null}
        </Flex>
      </Flex>
    );
  };

  return (
    <Flex
      w="100%"
      maxW="1040px"
      minH={{ base: '300px', md: '540px' }}
      bg="#FFF9D0"
      borderRadius="10px"
      direction="column"
      align="center"
      justify="flex-start"
      pt="40px"
      pb="40px"
      px="20px"
      gap="0"
    >
      {/* 驗證成功圖示 */}
      <Box w="200px" h="200px">
        <img src={verifySuccessIcon} alt="verify success" width="200" height="200" />
      </Box>

      {/* 驗證成功文字 */}
      <Text
        mt="10px"
        fontFamily="'Open Sans'"
        fontWeight={600}
        fontSize="20px"
        lineHeight="120%"
        letterSpacing="0.01em"
        color="#28A138"
        style={{ color: '#28A138' }}
      >
        {t('application.verifySuccess')}
      </Text>

      {/* 白色表格區塊 */}
      <Flex
        direction="column"
        align="center"
        w="100%"
        maxW="840px"
        bg="#FFFFFF"
        pt="35px"
        px="10px"
        gap="15px"
        mt="22px"
      >
        {/* 標題 */}
        <Text
          fontFamily="'Open Sans'"
          fontWeight={600}
          fontSize="20px"
          lineHeight="120%"
          letterSpacing="0.01em"
          color="#000000"
          style={{ color: '#000000' }}
        >
          {t('application.metadataTitle')}
        </Text>

        {/* 兩欄表格 */}
        <Flex direction="column" w="820px">
          {Array.from({ length: Math.ceil(entries.length / 2) }, (_, rowIdx) => {
            const left = entries[rowIdx * 2];
            const right = entries[rowIdx * 2 + 1];
            return (
              <Flex key={rowIdx} direction="row" align="stretch" w="820px">
                <Flex direction="row" align="stretch" w="410px">
                  {renderCell(left, true)}
                  {renderCell(left, false)}
                </Flex>
                <Flex direction="row" align="stretch" w="410px">
                  {renderCell(right, true)}
                  {renderCell(right, false)}
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      </Flex>

      {/* 按鈕列 */}
      <Flex direction="row" align="center" justify="center" gap="12px" mt="22px">
        <Button {...btnOutlineBlue} onClick={onBack}>
          {t('application.back')}
        </Button>
        <Button {...btnOutlineRed} onClick={onDataError}>
          {t('application.dataError')}
        </Button>
        <Button {...btnSolidBlue} onClick={onConfirm}>
          {t('application.dataCorrect')}
        </Button>
      </Flex>
    </Flex>
  );
};

/* 申請完成（成功）面板 */
interface ApplicationSuccessPanelProps {
  qrCode: string;
  onBack: () => void;
}

const ApplicationSuccessPanel: React.FC<ApplicationSuccessPanelProps> = ({ qrCode, onBack }) => {
  const { t } = useTranslation();
  const [secondsLeft, setSecondsLeft] = useState(600);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const minutes = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const seconds = String(secondsLeft % 60).padStart(2, '0');
  const isExpired = secondsLeft <= 0;

  return (
    <MainPanel>
      <Flex direction="column" align="center" gap="15px" w="406px">
        {/* QR Code + 倒數 + 申請成功文字 */}
        <Flex direction="column" align="center">
          <Box w="200px" h="200px" bg="#FFFFFF" flexShrink={0} opacity={isExpired ? 0.3 : 1} transition="opacity 0.3s">
            <img src={qrCode} alt="QR Code" width="200" height="200" />
          </Box>
          <Text
            mt="12px"
            fontFamily="'Open Sans'"
            fontWeight={600}
            fontSize="20px"
            lineHeight="120%"
            letterSpacing="0.05em"
            color={isExpired ? '#D21C1B' : '#00AAFF'}
            style={{ color: isExpired ? '#D21C1B' : '#00AAFF' }}
          >
            {isExpired ? '已過期' : `${minutes}:${seconds}`}
          </Text>
          <Text
            mt="6px"
            fontFamily="'Open Sans'"
            fontWeight={400}
            fontSize="12px"
            lineHeight="150%"
            letterSpacing="0.01em"
            color="#4F5E71"
            style={{ color: '#4F5E71' }}
            textAlign="center"
          >
            {t('application.qrCodeHint')}
          </Text>
          <Flex justify="center" align="center" pt="10px">
            <Text
              fontFamily="'Open Sans'"
              fontWeight={600}
              fontSize="30px"
              lineHeight="133%"
              letterSpacing="0.01em"
              textAlign="center"
              color="#28A138"
              style={{ color: '#28A138' }}
            >
              {t('application.applySuccess')}
            </Text>
          </Flex>
        </Flex>

        {/* 螢光底提示文字 + 返回按鈕 */}
        <Flex direction="column" align="center" gap="20px">
          <Flex align="center" justify="center" px="5px" py="5px" bg="#FFE643" borderRadius="2px" flexShrink={0}>
            <Text
              fontFamily="'Open Sans'"
              fontWeight={600}
              fontSize="20px"
              lineHeight="120%"
              letterSpacing="0.01em"
              textAlign="center"
              color="#000000"
              style={{ color: '#000000' }}
              whiteSpace="nowrap"
            >
              {t('application.applySuccessHint')}
            </Text>
          </Flex>

          {/* 返回首頁：先跳確認 */}
          <Button {...btnOutlineBlue} onClick={() => setShowConfirm(true)}>
            {t('application.backHome')}
          </Button>
        </Flex>
      </Flex>

      {/* 確認 Dialog（覆蓋在面板上方） */}
      {showConfirm && (
        <Flex position="fixed" inset={0} bg="rgba(0,0,0,0.45)" zIndex={9997} align="center" justify="center">
          <Flex
            direction="column"
            align="center"
            bg="#FFFFFF"
            borderRadius="10px"
            px="32px"
            py="28px"
            gap="20px"
            maxW="480px"
            w="90%"
          >
            {/* 說明文字 */}
            <Text
              fontFamily="'Open Sans'"
              fontWeight={600}
              fontSize="18px"
              lineHeight="150%"
              letterSpacing="0.01em"
              textAlign="center"
              color="#000000"
            >
              {t('application.confirmLeaveHint')}
            </Text>

            {/* 按鈕列 */}
            <Flex direction="row" gap="12px">
              {/* 取消：留在此頁 */}
              <Button {...btnOutlineBlue} onClick={() => setShowConfirm(false)}>
                {t('application.cancel')}
              </Button>
              {/* 確認：真正離開 */}
              <Button {...btnSolidBlue} onClick={onBack}>
                {t('application.confirmLeave')}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      )}
    </MainPanel>
  );
};

/* 申請完成（失敗）面板 */
interface ApplicationFailPanelProps {
  onRetry: () => void | Promise<void>;
  onBack: () => void;
  errorMsg: string;
}

/* 失敗圖示（inline SVG） */
const FailIllustration: React.FC = () => (
  <Box w="200px" h="200px" display="flex" alignItems="center" justifyContent="center">
    <svg width="200" height="200" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" fill="none">
      {/* 外框圓，白底紅邊 */}
      <circle cx="100" cy="100" r="72.5" fill="#FFFFFF" stroke="#D21C1B" strokeWidth="5" />
      {/* 驚嘆號直線 */}
      <line x1="100" y1="72.62" x2="100" y2="108.2" stroke="#D21C1B" strokeWidth="9.51" strokeLinecap="round" />
      {/* 驚嘆號圓點 */}
      <circle cx="100" cy="125.98" r="4.76" fill="#D21C1B" />
    </svg>
  </Box>
);

const ApplicationFailPanel: React.FC<ApplicationFailPanelProps> = ({ onRetry, onBack, errorMsg }) => {
  const { t } = useTranslation();

  return (
    <MainPanel>
      <Flex direction="column" align="center" gap="15px" w="485px">
        {/* 失敗圖示 + 標題 */}
        <Flex direction="column" align="center">
          <FailIllustration />
          <Flex justify="center" align="center" p="10px">
            <Text
              fontFamily="'Open Sans'"
              fontWeight={600}
              fontSize="30px"
              lineHeight="133%"
              letterSpacing="0.01em"
              textAlign="center"
              color="#D21C1B"
            >
              {t('application.applyFail')}
            </Text>
          </Flex>
        </Flex>

        {/* 錯誤訊息 + 按鈕列 */}
        <Flex direction="column" align="center" gap="20px" w="485px">
          <Text
            fontFamily="'Open Sans'"
            fontWeight={600}
            fontSize="20px"
            lineHeight="120%"
            letterSpacing="0.01em"
            textAlign="center"
            color="#D21C1B"
            style={{ color: '#D21C1B' }}
            w="485px"
            mb="8px"
          >
            {errorMsg}
          </Text>
          <Flex direction="row" justify="center" align="center" gap="12px">
            <Button {...btnSolidBlue} onClick={() => void onRetry()}>
              {t('application.retry')}
            </Button>
            <Button {...btnOutlineBlue} onClick={onBack}>
              {t('application.back')}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </MainPanel>
  );
};

/* 主元件 */
const UploadSection: React.FC<UploadSectionProps> = ({ status, setStatus, showMetadata, setShowMetadata }) => {
  const [toast, setToast] = useState<ToastType>(null);
  const [loading, setLoading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [codeErrorMsg, setCodeErrorMsg] = useState('');
  const [applyErrorMsg, setApplyErrorMsg] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [metadata, setMetadata] = useState<Record<string, string>>({});
  const [identifier, setIdentifier] = useState('');
  //const [verifiedCode, setVerifiedCode] = useState('');
  const [failReason, setFailReason] = useState<FailReason>(null);

  const closeToast = useCallback(() => setToast(null), []);

  /* 重置所有狀態，回到第一步 */
  const resetToFirst = useCallback(() => {
    setPendingFile(null);
    setCodeErrorMsg('');
    setApplyErrorMsg('');
    setQrCode('');
    setShowMetadata(false);
    setMetadata({});
    setIdentifier('');
    //setVerifiedCode('');
    setFailReason(null);
    setStatus(0);
  }, [setStatus, setShowMetadata]);

  /* 步驟一：選檔 → 驗證格式 → 跳步驟二 */
  const handleFile = useCallback(
    async (file: File) => {
      if (file.size / 1048576 > MAX_MB) {
        setToast('overSize');
        return;
      }
      const isValid = await validateCertPdf(file).catch(() => false);
      if (!isValid) {
        setToast('invalidFormat');
        return;
      }
      setPendingFile(file);
      setCodeErrorMsg('');
      setShowMetadata(false);
      setStatus(1);
    },
    [setStatus, setShowMetadata],
  );

  /* 步驟二：輸入驗證碼 → 呼叫 getVcMetadata → 成功後顯示 metadata */
  const handleConfirm = useCallback(
    async (code: string) => {
      if (!pendingFile) return;
      setLoading(true);
      setCodeErrorMsg('');
      //setVerifiedCode(code);
      try {
        const res = await getVcMetadata(pendingFile, code);
        if (!res) {
          setToast('networkError');
          return;
        }
        if (!res.applied) {
          setCodeErrorMsg(res.error_msg ?? '驗證失敗');
          return;
        }
        setIdentifier(res.identifier ?? '');
        setMetadata(Object.fromEntries(Object.entries(res.augmented_metadata ?? {}).filter(([, v]) => v !== '')));
        setShowMetadata(true);
      } catch {
        setToast('networkError');
      } finally {
        setLoading(false);
      }
    },
    [pendingFile, setShowMetadata],
  );

  /* 步驟三：確認 metadata → 呼叫 getVcQrcode → 成功跳步驟四(成功)，失敗跳步驟四(失敗) */
  const handleFinalConfirm = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getVcQrcode(identifier);
      if (!res) {
        setFailReason('network');
        setApplyErrorMsg('因網路中斷，請確認網路連線是否異常，並重新申請。');
        setShowMetadata(false);
        setStatus(3);
      } else if (res.applied && res.qr_code) {
        setQrCode(res.qr_code);
        setShowMetadata(false);
        setFailReason(null);
        setStatus(2);
      } else {
        setFailReason('server');
        setApplyErrorMsg(res.error_msg ?? '申請失敗，請稍後再試。');
        setShowMetadata(false);
        setStatus(3);
      }
    } catch {
      setFailReason('network');
      setApplyErrorMsg('因網路中斷，請確認網路連線是否異常，並重新申請。');
      setShowMetadata(false);
      setStatus(3);
    } finally {
      setLoading(false);
    }
  }, [identifier, setStatus, setShowMetadata]);

  /* 申請失敗重試：依 failReason 決定重試哪個 API */
  const handleRetry = useCallback(async () => {
    setCodeErrorMsg('');
    setApplyErrorMsg('');

    /* server 錯誤 → 回步驟二重新輸入驗證碼 */
    if (failReason !== 'network') {
      setFailReason(null);
      setShowMetadata(false);
      setStatus(1);
      return;
    }

    /* network 錯誤 → 重試 getVcQrcode */
    setLoading(true);
    try {
      const res = await getVcQrcode(identifier);
      if (!res) {
        setApplyErrorMsg('因網路中斷，請確認網路連線是否異常，並重試。');
        setStatus(3);
      } else if (res.applied && res.qr_code) {
        setQrCode(res.qr_code);
        setFailReason(null);
        setStatus(2);
      } else {
        setApplyErrorMsg(res.error_msg ?? '申請失敗，請稍後再試。');
        setStatus(3);
      }
    } catch {
      setApplyErrorMsg('因網路中斷，請確認網路連線是否異常，並重試。');
      setStatus(3);
    } finally {
      setLoading(false);
    }
  }, [failReason, identifier, setStatus, setShowMetadata]);

  return (
    <Flex w="100%" direction="column" align="center">
      {/* 各步驟面板 */}
      {status === 0 && (
        <UploadPdfPanel onFile={handleFile} onBrowse={closeToast} onMultipleFiles={() => setToast('multipleFiles')} />
      )}
      {status === 1 && !showMetadata && (
        <IdentityVerificationPanel
          onBack={() => {
            setPendingFile(null);
            setCodeErrorMsg('');
            setShowMetadata(false);
            setStatus(0);
          }}
          onConfirm={handleConfirm}
          codeErrorMsg={codeErrorMsg}
          onForgotCode={() => setToast('forgotCode')}
        />
      )}
      {status === 1 && showMetadata && (
        <MetadataConfirmPanel
          metadata={metadata}
          onConfirm={handleFinalConfirm}
          onBack={resetToFirst}
          onDataError={() => setToast('dataError')}
        />
      )}
      {status === 2 && <ApplicationSuccessPanel qrCode={qrCode} onBack={resetToFirst} />}
      {status === 3 && <ApplicationFailPanel onRetry={handleRetry} onBack={resetToFirst} errorMsg={applyErrorMsg} />}

      {/* Loading mask（全螢幕遮罩） */}
      {loading && (
        <Flex position="fixed" inset={0} bg="rgba(0,0,0,0.35)" zIndex={9998} align="center" justify="center">
          <Spinner w="60px" h="60px" thickness="5px" speed="0.75s" color="#00AAFF" emptyColor="rgba(255,255,255,0.3)" />
        </Flex>
      )}

      {/* Toast（底部置中） */}
      {toast && (
        <Flex position="fixed" bottom="32px" left="50%" transform="translateX(-50%)" zIndex={9999}>
          {toast === 'overSize' && (
            <Toast
              variant="critical"
              title="檔案上傳失敗"
              description="請確認檔案格式須為.pdf檔案，且檔案大小不超過2mb。"
              onClose={closeToast}
            />
          )}
          {toast === 'networkError' && (
            <Toast
              variant="critical"
              title="網路異常"
              description="網路異常，請確認網路連線，並重新上傳檔案。"
              onClose={closeToast}
            />
          )}
          {toast === 'forgotCode' && (
            <Toast
              variant="info"
              title="忘記代碼？"
              description={`請查看由發證單位寄發的數位證書電子郵件，確認驗證代碼。\n如信件遺失，請洽原發證單位。`}
              onClose={closeToast}
            />
          )}
          {toast === 'invalidFormat' && (
            <Toast
              variant="critical"
              title="檔案格式不合，無法使用此服務"
              description="請確認上傳的是符合規格的 PDF 檔案。"
              onClose={closeToast}
            />
          )}
          {toast === 'dataError' && (
            <Toast
              variant="info"
              title="資料有誤？"
              description="若個人資料有誤，請聯繫原畢業學校進行資料確認與處理。"
              onClose={closeToast}
            />
          )}
          {toast === 'multipleFiles' && (
            <Toast
              variant="critical"
              title="檔案上傳失敗"
              description="一次只能上傳一個檔案，請重新拖曳單一 PDF 檔案。"
              onClose={closeToast}
            />
          )}
        </Flex>
      )}
    </Flex>
  );
};

export default UploadSection;
